import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resendVerification, listDomains } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { SavingsBand } from '../components/dashboard/SavingsBand';
import { RealizedSavingsTrend } from '../components/dashboard/RealizedSavingsTrend';
import { UtilizationScoreTrend } from '../components/dashboard/UtilizationScoreTrend';
import { SavingsByCategory } from '../components/dashboard/SavingsByCategory';
import { CostOptimizationStatus } from '../components/dashboard/CostOptimizationStatus';
import { DomainLeaderboard } from '../components/dashboard/DomainLeaderboard';
import { OrgSpend } from '../components/dashboard/OrgSpend';
import { ShoutOuts } from '../components/dashboard/ShoutOuts';
import { DomainsConnectionStatus } from '../components/dashboard/DomainsConnectionStatus';
import { OrgCompliancePosture } from '../components/dashboard/CompliancePosture';

/**
 * Organization dashboard — the primary post-auth landing page (DASH-001..011).
 * An executive overview for the whole org: the savings headline, the realized-
 * savings trend, a gamified domains leaderboard, shout-outs, and domain
 * health + connection status. Data refreshes on its own (React Query intervals),
 * and every widget deep-links into detail.
 */
export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const [resent, setResent] = useState(false);
  // The API returns only the domains this user can access (all of them for an
  // ADMINISTRATOR; the granted subset for everyone else), so this count makes the
  // dashboard's scope explicit for every role.
  const { data: domains } = useQuery({
    queryKey: ['domains'],
    queryFn: listDomains,
  });
  const envCount = domains?.length ?? 0;

  async function onResend() {
    await resendVerification();
    setResent(true);
  }

  return (
    <div className="space-y-6">
      {user && !user.emailVerified && (
        <Alert variant="warning">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>
              <b className="text-bone">Verify your email</b> to unlock all features.
            </span>
            {resent ? (
              <span className="text-warning">Verification email sent.</span>
            ) : (
              <Button size="sm" variant="outline" onClick={onResend}>
                Resend email
              </Button>
            )}
          </div>
        </Alert>
      )}

      <div>
        <h1 className="font-display text-2xl font-bold">{user?.organizationName ?? 'Dashboard'}</h1>
        <p className="text-sm text-muted">
          Welcome{user?.name ? `, ${user.name}` : ''} — here&apos;s how your organization is doing.
        </p>
        {envCount > 0 && (
          <p className="mt-1 text-xs text-muted">
            Aggregated across the {envCount} domain{envCount === 1 ? '' : 's'} you can access.
          </p>
        )}
      </div>

      <OrgSpend />
      <SavingsBand />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <RealizedSavingsTrend />
        <UtilizationScoreTrend />
      </div>
      <SavingsByCategory scope="org" />
      <CostOptimizationStatus scope="org" />
      <OrgCompliancePosture />
      <DomainLeaderboard />
      <ShoutOuts />
      <DomainsConnectionStatus />
    </div>
  );
}
